import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ModerationReportContextType } from '../../../common/moderationTypes';
import { LoadableModerationReportModal } from './LoadableModerationReportModal';
import { useModerationReportingEnabled } from './useModerationReportingEnabled';

export const OPEN_REPORT_MODAL_EVENT = 'rio:open-report-modal';

/**
 * Query param that opens the report modal on page load, e.g.
 * `https://raider.io/?open_moderation=1&utm_source=client`. The desktop client
 * uses it to hand a report off to the website; the `utm_source=client` it already
 * appends to every link it opens is what the modal reads to tag the report.
 */
export const OPEN_REPORT_MODAL_QUERY_PARAM = 'open_moderation';

export function openGlobalReportModal() {
  window.dispatchEvent(new Event(OPEN_REPORT_MODAL_EVENT));
}

export function GlobalReportModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const canReport = useModerationReportingEnabled();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!canReport) {
      return;
    }

    const handler = () => {
      setHasOpened(true);
      setIsOpen(true);
    };

    window.addEventListener(OPEN_REPORT_MODAL_EVENT, handler);
    return () => window.removeEventListener(OPEN_REPORT_MODAL_EVENT, handler);
  }, [canReport]);

  // Deep link: ?open_moderation=1 opens the modal on load. The trigger param is
  // only stripped together with the open (a reload should not re-open the modal),
  // so a gate that flips to true late still gets its chance; the rest of the query
  // — utm_source included — is preserved for source_url and the client marker.
  useEffect(() => {
    if (!canReport) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (params.get(OPEN_REPORT_MODAL_QUERY_PARAM) !== '1') {
      return;
    }

    setHasOpened(true);
    setIsOpen(true);

    params.delete(OPEN_REPORT_MODAL_QUERY_PARAM);
    const remaining = params.toString();
    navigate(
      { pathname: location.pathname, search: remaining ? `?${remaining}` : '', hash: location.hash },
      { replace: true, state: { disableScroll: true } }
    );
  }, [canReport, location.hash, location.pathname, location.search, navigate]);

  if (!canReport || !hasOpened) {
    return null;
  }

  return (
    <LoadableModerationReportModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      contextType={ModerationReportContextType.OTHER}
      entityId={0}
    />
  );
}
